import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertProps,
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  FormField,
  Header,
  Modal,
  Spinner,
  Textarea,
} from '@amzn/awsui-components-react-v3';
import {
  BASELINE_ACTION_DELETE,
  BASELINE_ACTION_KEEP,
  BASELINE_TYPE_DATA_PERMISSION_CONSUMER,
  fetchBaselineNeededConsumerRedshiftRoleToUserDataPermissions,
  RESOURCE_USER_BASELINE,
} from 'src/components/permissions/myBaselining/baseliningUtils';
import {
  AWS_DATA_HANDLING_STANDARDS,
  GROUP_TYPE_REDSHIFT_MARKETPLACE_DATA_PERMISSION_BASELINE_ONLY,
  MARKETPLACE_DATA_ACCESS_STANDARD_URL,
  MARKETPLACE_FAQ_URL,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import { isValidWorkspace } from 'src/commons/validationUtils';
import { getGroupInfo } from 'src/api/permissions';
import { submitBaseline } from 'src/api/notifications';
import { SubmitBaselineRequest } from 'aws-sdk/clients/awsdlomni';
import { Link } from 'src/components/common/Link';

export interface BaselineDataAccessPermissionProps {
  setContentType: any;
  match: any;
  username: string;
}

export const BaselineDataAccessPermission = (props: BaselineDataAccessPermissionProps) => {
  const useCaseFormat = 'I need access to AWS Marketplace data to [explain your specific business purpose here]. In my role as [your position], I will use this data for [intended use case]. This access will help achieve [business outcomes] over [timeframe needed]. The specific data I require includes [mention tables needed].'

  const [keepLoading, setKeepLoading] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState('success' as AlertProps.Type);
  const [alertMessage, setAlertMessage] = useState('There are no pending access to review');
  const [useCase, setUseCase] = useState(useCaseFormat);
  const [marketplaceDataAccessStandardsLinkClicked, setMarketplaceDataAccessStandardsLinkClicked] = useState(false);
  const [awsDataHandlingStandardsLinkClicked, setAwsDataHandlingStandardsLinkClicked] = useState(false);
  const [acknowledgedNoDownload, setAcknowledgedNoDownload] = useState(false);
  const [acknowledgedNoDetailedReports, setAcknowledgedNoDetailedReports] = useState(false);
  const [acknowledgedNoSharingOutsideL8, setAcknowledgedNoSharingOutsideL8] = useState(false);
  const [acknowledgedDataHandlingStandards, setAcknowledgedDataHandlingStandards] = useState(false);
  const [acknowledgedMarketplaceDataAccessStandards, setAcknowledgedMarketplaceDataAccessStandards] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBaseline, setLoadingBaseline] = useState(true);
  const [mpDataPermissions, setMPDataPermissions] = useState([]);
  const [useCaseErrorMsg, setUseCaseErrorMsg] = useState('');
  const [termsAndConditionsErrorMsg, setTermsAndConditionsErrorMsg] = useState('');

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [props.match.params.ownerId]);

  const handleRefresh = async () => {
    try {
      if (!props.match.params.ownerId) return;
      const ownerId = props.match.params.ownerId;
      if (!isValidWorkspace(ownerId)) {
        const ownerGroup = await getGroupInfo({ groupId: ownerId });
        if (ownerGroup?.config?.groupType == GROUP_TYPE_REDSHIFT_MARKETPLACE_DATA_PERMISSION_BASELINE_ONLY) {
          handleMPRefresh();
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMPRefresh = async () => {
    const ownerId = props.match.params.ownerId;
    let dps = await fetchBaselineNeededConsumerRedshiftRoleToUserDataPermissions(ownerId);
    dps = dps.filter((dp) => dp?.activatedBy == props.username);
    if (dps.length === 0) {
      setAlertType('error');
      setAlertVisible(true);
      setAlertMessage('You must be an active user of AWSDW owned Redshift cluster(s).');
      setLoadingBaseline(false);
      return;
    }
    setMPDataPermissions(dps);
    // Pre-populate data if already exists. All data permissions from the same user have the same use case
    if (dps[0]?.baselineInfo?.useCase) {
      const dpUseCase = JSON.parse(dps[0]?.baselineInfo?.useCase);
      setUseCase(dpUseCase?.useCase ?? useCaseFormat);
      setAcknowledgedNoDownload(!!dpUseCase?.acknowledgedNoDownload);
      setAcknowledgedNoDetailedReports(!!dpUseCase?.acknowledgedNoDetailedReports);
      setAcknowledgedNoSharingOutsideL8(!!dpUseCase?.acknowledgedNoSharingOutsideL8);
      setAcknowledgedDataHandlingStandards(!!dpUseCase?.acknowledgedDataHandlingStandards);
      setAcknowledgedMarketplaceDataAccessStandards(!!dpUseCase?.acknowledgedMarketplaceDataAccessStandards);
    }
    setModalVisible(true);
    setLoadingBaseline(false);
  };

  const isValidForm = () => {
    const validations = [
      {
        condition: !useCase || useCase == useCaseFormat,
        setError: setUseCaseErrorMsg,
        errorMessage: 'Business use case cannot be empty',
      },
      {
        condition: !(
          acknowledgedNoDownload &&
          acknowledgedNoDetailedReports &&
          acknowledgedNoSharingOutsideL8 &&
          acknowledgedDataHandlingStandards &&
          acknowledgedMarketplaceDataAccessStandards
        ),
        setError: setTermsAndConditionsErrorMsg,
        errorMessage:
          'You must accept all terms and conditions to complete the access review. Make sure to open and review all links in the form.',
      },
    ];

    let isValid = true;

    validations.forEach(({ condition, setError, errorMessage }) => {
      if (condition) {
        setError(errorMessage);
        isValid = false;
      } else {
        setError('');
      }
    });

    return isValid;
  };

  const handleSubmitBaseline = async (request: SubmitBaselineRequest, successMessage: string): Promise<void> => {
    setKeepLoading(true);
    try {
      await submitBaseline(request);
      setAlertType('success');
      setAlertMessage(successMessage);
    } catch (error) {
      setAlertType('error');
      setAlertMessage('Failed to submit the form. Please try again later, or contact Omni oncall.');
      console.error(error);
    } finally {
      setAlertVisible(true);
      setKeepLoading(false);
      setModalVisible(false);
    }
  };

  const handleMPRelinquish = async () => {
    await handleSubmitBaseline(
      {
        baselineAction: BASELINE_ACTION_DELETE,
        baselineType: RESOURCE_USER_BASELINE,
        resourceType: BASELINE_TYPE_DATA_PERMISSION_CONSUMER,
        resourcesToBaseline: mpDataPermissions.map((dp) => ({ id: dp.dataPermissionId })),
      },
      'Relinquish request for access to Marketplace specific records has been submitted successfully',
    );
  };

  const handleMPKeep = async () => {
    if (!isValidForm()) {
      return;
    }
    await handleSubmitBaseline(
      {
        baselineAction: BASELINE_ACTION_KEEP,
        baselineType: RESOURCE_USER_BASELINE,
        resourceType: BASELINE_TYPE_DATA_PERMISSION_CONSUMER,
        resourcesToBaseline: mpDataPermissions.map((dp) => ({ id: dp.dataPermissionId })),
        baselineInfo: { useCase: buildMPUseCaseJsonString() },
      },
      'Successfully submitted the form. You can now access Marketplace specific records in your queries within AWSDW owned Redshift cluster(s)',
    );
  };

  const dismissModal = () => {
    setModalVisible(false);
    setAlertVisible(true);
    setAlertMessage('No action has been performed, refresh the page to continue with the review.');
    setAlertType('info');
  };

  const buildMPUseCaseJsonString = () => {
    return JSON.stringify({
      acknowledgedDataHandlingStandards,
      acknowledgedNoDownload,
      acknowledgedNoDetailedReports,
      acknowledgedNoSharingOutsideL8,
      acknowledgedMarketplaceDataAccessStandards,
      useCase: useCase.trim(),
    });
  };

  return (
    <>
      {loadingBaseline && <Spinner size='large'></Spinner>}
      {alertVisible && (
        <Alert type={alertType} dismissible={false}>
          {alertMessage}
        </Alert>
      )}
      <Modal
        visible={modalVisible}
        onDismiss={dismissModal}
        size='max'
        header={
          <Header
            variant='h2'
            description='You have access to AWS Marketplace (MP) data in AWSDW redshift clusters. Users with access to
            MP data are required to provide an up to date business justification.'
            info={
              <Link external={true} href={MARKETPLACE_FAQ_URL}>
                info
              </Link>
            }
          >
            AWS Data Platform Marketplace data access
          </Header>
        }
        footer={
          <Box float='right'>
            <SpaceBetween size='m' direction='horizontal'>
              <Button variant='normal' loading={keepLoading} onClick={handleMPRelinquish}>
                Relinquish access to Marketplace records
              </Button>
              <Button variant='primary' loading={keepLoading} onClick={handleMPKeep}>
                Keep access to Marketplace records
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size='m' direction='vertical'>
          <ColumnLayout>
            <FormField
              label='Business use case'
              description={
                <>
                  Provide a detailed explanation of why you need access to AWS Marketplace data. Include your project
                  goals, data requirements, and how this access aligns with your business objectives.{' '}
                  <i>Incomplete business use case may lead to access being revoked</i>.
                </>
              }
              errorText={useCaseErrorMsg}
            >
              <Textarea
                onChange={(e) => setUseCase(e.detail.value)}
                value={useCase}
                placeholder={useCaseFormat}
              />
            </FormField>
            <FormField
              label='Terms and conditions'
              description={'Review and acknowledge the terms and conditions of accessing AWS Marketplace data.'}
              errorText={termsAndConditionsErrorMsg}
            >
              <Checkbox
                checked={acknowledgedNoDownload}
                onChange={({ detail }) => setAcknowledgedNoDownload(detail.checked)}
              >
                I will not store Marketplace data to my laptop.
              </Checkbox>
              <Checkbox
                checked={acknowledgedNoDetailedReports}
                onChange={({ detail }) => setAcknowledgedNoDetailedReports(detail.checked)}
              >
                I will not display detailed Marketplace data, including product details, vendor information, or
                descriptive content.
              </Checkbox>
              <Checkbox
                checked={acknowledgedNoSharingOutsideL8}
                onChange={({ detail }) => setAcknowledgedNoSharingOutsideL8(detail.checked)}
              >
                I will not share Marketplace data or any related reports with anyone outside my L8 organization.
              </Checkbox>
              <Checkbox
                checked={acknowledgedDataHandlingStandards}
                onChange={({ detail }) => setAcknowledgedDataHandlingStandards(detail.checked)}
                disabled={!(awsDataHandlingStandardsLinkClicked || acknowledgedDataHandlingStandards)}
              >
                I will only store or process data in production environments that meet{' '}
                <Link
                  external={true}
                  href={AWS_DATA_HANDLING_STANDARDS}
                  onClick={() => setAwsDataHandlingStandardsLinkClicked(true)}
                >
                  AWS Data Handling Standard requirements
                </Link>{' '}
                requirements.
              </Checkbox>
              <Checkbox
                checked={acknowledgedMarketplaceDataAccessStandards}
                onChange={({ detail }) => setAcknowledgedMarketplaceDataAccessStandards(detail.checked)}
                disabled={!(marketplaceDataAccessStandardsLinkClicked || acknowledgedMarketplaceDataAccessStandards)}
              >
                I understand the{' '}
                <Link
                  external={true}
                  href={MARKETPLACE_DATA_ACCESS_STANDARD_URL}
                  onClick={() => setMarketplaceDataAccessStandardsLinkClicked(true)}
                >
                  Marketplace Data Access Standards
                </Link>{' '}
                and confirm my use case meets all guidelines.
              </Checkbox>
            </FormField>
          </ColumnLayout>
        </SpaceBetween>
      </Modal>
    </>
  );
};
